import { FC, useEffect, useState } from "react";
import {
  Autocomplete,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useSelectedChallengeContext } from "@context/SelectedChallengeContext";
import { getChallengeTypeTranslation } from "@/utils/challenge-utils";
import { useResponsiveContext } from "@context/ResponsiveContext";
import { SAMS_CHALLENGE_TYPES, SamsChallengeType } from "@/models/gameState";

interface AutocompleteValue {
  id: SamsChallengeType;
  label: string;
}

const ChallengeTypeSelector: FC = () => {
  const { onTablet, isTouchScreen } = useResponsiveContext();
  const {
    challenge: { challenger, challengeMoment, challengeType },
    setTypeOfChallenge,
  } = useSelectedChallengeContext();
  const [inputValue, setInputValue] = useState(
    challengeType ? getChallengeTypeTranslation(challengeType) : "",
  );
  const [value, setValue] = useState<AutocompleteValue | undefined>(() => {
    const ct = SAMS_CHALLENGE_TYPES.find((cType) => cType === challengeType);
    return ct
      ? {
          id: ct,
          label: getChallengeTypeTranslation(ct),
        }
      : undefined;
  });

  useEffect(() => {
    const ct = SAMS_CHALLENGE_TYPES.find((cType) => cType === challengeType);
    setValue(
      ct
        ? {
            id: ct,
            label: getChallengeTypeTranslation(ct),
          }
        : undefined,
    );
  }, [challengeType]);

  const invertibleChallengesAvailable =
    challenger !== "referee" && challengeMoment !== "DURING_RALLY";

  const challengeTypeOptions = SAMS_CHALLENGE_TYPES.map((cType) => ({
    id: cType,
    label: getChallengeTypeTranslation(cType),
  }));

  return (
    <Card variant="elevation" elevation={5} sx={{ width: "600px", mx: 2 }}>
      <CardHeader
        title={"Challenge Situation"}
        titleTypographyProps={{ variant: "subtitle1", align: "center" }}
        sx={{ pb: 0 }}
      />
      <CardContent>
        {onTablet || isTouchScreen ? (
          <Select
            variant={"outlined"}
            fullWidth
            value={challengeType || ""}
            onChange={(e: SelectChangeEvent<SamsChallengeType>) => {
              if (e.target.value) {
                setTypeOfChallenge(e.target.value as SamsChallengeType);
              }
            }}
          >
            {challengeTypeOptions.map((c, index, types) => (
              <MenuItem value={c.id} key={c.id}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Autocomplete
            renderInput={(params) => <TextField {...params} />}
            options={challengeTypeOptions}
            renderOption={(props, c) => {
              const { key, ...optionProps } = props;
              return (
                <MenuItem key={c.id} {...optionProps} value={c.id}>
                  {c.label}
                </MenuItem>
              );
            }}
            onChange={(event, value) => {
              if (value) {
                setTypeOfChallenge(
                  (value.id as SamsChallengeType) ?? undefined,
                );
                setValue(value);
              }
            }}
            value={value}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ChallengeTypeSelector;
